/**
 * Axios istemcisi — JWT access token yönetimi ve refresh interceptor.
 * Access token in-memory (Zustand store). Refresh token HttpOnly cookie üzerinden.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import i18n from '../i18n'

const DEVICE_ERRORS = ['DEVICE_REQUIRED', 'DEVICE_UNKNOWN', 'DEVICE_PENDING', 'DEVICE_REVOKED', 'IP_NOT_ALLOWED']

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // refresh token cookie için
  headers: { 'Content-Type': 'application/json' },
})

// Access token in-memory (XSS'e karşı güvenli, kısa ömürlü)
// Refresh token: "beni hatırla" seçilmişse localStorage (kalıcı), aksi hâlde sessionStorage (sekme bazlı)
let _accessToken: string | null = null
let _refreshToken: string | null =
  localStorage.getItem('_rt') ?? sessionStorage.getItem('_rt')

export function setAccessToken(token: string | null): void {
  _accessToken = token
}

export function getAccessToken(): string | null {
  return _accessToken
}

/**
 * @param token   Refresh token değeri (null = sil)
 * @param persistent  true → localStorage (beni hatırla), false/undefined → sessionStorage
 */
export function setRefreshToken(token: string | null, persistent?: boolean): void {
  _refreshToken = token
  if (token) {
    if (persistent) {
      localStorage.setItem('_rt', token)
      sessionStorage.removeItem('_rt')
    } else {
      sessionStorage.setItem('_rt', token)
      localStorage.removeItem('_rt')
    }
  } else {
    localStorage.removeItem('_rt')
    sessionStorage.removeItem('_rt')
  }
}

export function getRefreshToken(): string | null {
  return _refreshToken
}

// Request interceptor — her isteğe token ekle
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`
  }
  return config
})

// Response interceptor — 401'de token yenile
let _refreshPromise: Promise<string | null> | null = null

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalReq = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      suppressErrorToast?: boolean
    }

    // Doğrulama hatası: sunucunun alan ayrıntılarını mesaja ekle (çağıran yerler yalnızca error.message okur; "Geçersiz bilgiler" tek başına işe yaramaz)
    const apiErr = (error.response?.data as { error?: { message?: string; details?: Record<string, string[]> } } | undefined)?.error
    if (apiErr?.details) {
      const lines = Object.entries(apiErr.details).flatMap(([field, msgs]) => (Array.isArray(msgs) ? msgs : []).map((m) => `${field}: ${m}`)).slice(0, 5)
      if (lines.length) apiErr.message = `${apiErr.message ?? ''} ${lines.join('; ')}`.trim()
    }

    // Masaüstü / cihaz korumaları — yazma işlemi engellendi
    if (error.response?.status === 403 || error.response?.status === 423) {
      const code = (error.response.data as { error?: { code?: string } } | undefined)?.error?.code
      // Masaüstü: lisans geçersiz → salt-okunur
      if (code === 'LICENSE_READONLY') {
        toast.error(i18n.t('desktop:readonly.toast'), { id: 'license-readonly' })
        return Promise.reject(error)
      }
      // Masaüstü Veri Merkezi cihaz koruması (tarayıcıdan erişim kapalı, cihaz onaysız/iptal, IP izni yok)
      if (code && DEVICE_ERRORS.includes(code)) {
        toast.error(i18n.t(`desktop:access.${code}`), { id: 'device-access' })
        return Promise.reject(error)
      }
    }

    if (error.response?.status === 401 && !originalReq._retry) {
      originalReq._retry = true

      // Aynı anda birden fazla refresh isteği gitmesin
      if (!_refreshPromise) {
        _refreshPromise = refreshAccessToken().finally(() => {
          _refreshPromise = null
        })
      }

      const newToken = await _refreshPromise
      if (newToken) {
        originalReq.headers.Authorization = `Bearer ${newToken}`
        return api(originalReq)
      }

      // Refresh başarısız — kullanıcıyı login'e yönlendir
      // Auth sayfalarından yönlendirme yapılmaz (sonsuz yenileme döngüsü önlenir)
      setAccessToken(null)
      setRefreshToken(null)
      const authPaths = ['/login', '/forgot-password', '/reset-password', '/activate']
      const isAuthPage = authPaths.some((p) => window.location.pathname.startsWith(p))
      if (!isAuthPage) {
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    // Global yazma-hatası bildirimi: sessiz kalan mutation hatalarını yüzeye çıkarır.
    // Yalnızca yazma metotlarında (POST/PUT/PATCH/DELETE), 401 dışında ve opt-out
    // (suppressErrorToast) edilmemişse. Böylece onError'u olmayan mutation'lar da
    // kullanıcıya hata gösterir. Kendi zengin/özel toast'ını gösteren yerler
    // `{ suppressErrorToast: true }` geçerek bu davranışı kapatabilir.
    const method = (originalReq?.method ?? 'get').toLowerCase()
    const isWrite = method === 'post' || method === 'put' || method === 'patch' || method === 'delete'
    if (isWrite && !originalReq?.suppressErrorToast) {
      const msg = (error.response?.data as { error?: { message?: string } } | undefined)?.error?.message
      toast.error(msg ?? 'İşlem başarısız oldu.')
    }

    return Promise.reject(error)
  },
)

async function refreshAccessToken(): Promise<string | null> {
  if (!_refreshToken) return null
  // Mevcut token localStorage'da mı? (beni hatırla) — rotasyondan sonra korunmalı
  const persistent = localStorage.getItem('_rt') !== null
  try {
    const res = await axios.post<{ data: { accessToken: string; refreshToken: string } }>(
      `${BASE_URL}/auth/refresh`,
      { refreshToken: _refreshToken },
      { withCredentials: true },
    )
    const { accessToken, refreshToken } = res.data.data
    setAccessToken(accessToken)
    setRefreshToken(refreshToken, persistent)
    return accessToken
  } catch {
    return null
  }
}
